// App
import type React from 'react';
import { memo } from 'react';

import { Box } from '@zillow/constellation-10';

// Components
import CompsCarouselCard from '../CompsCarouselCard';
import MyPropertyCard from '../MyPropertyCard';

// Actions
import { setActiveId, setHoverId } from '../flux/actions/UIActions';
import { useAppDispatch, useAppSelector } from '../flux/configureStore';

// Types
import type ComparablesModelType from '../models/Comparables';

type ItemPropTypes = {
  id: string | null;
  item: ComparablesModelType;
  itemRef: React.RefObject<HTMLDivElement>;
  idx: number;
};

/**
 * Determines whether an item is "striped" (gray if so, white if not).
 * The items adjacent to the MyPropertyCard should always be white.
 * Remaining items should alternate between gray and white from there.
 */
function isStriped(idx: number, userPropertyIdx: number): boolean {
  if (idx < userPropertyIdx) {
    return (userPropertyIdx - idx) % 2 === 0;
  } else if (idx > userPropertyIdx) {
    return (idx - userPropertyIdx) % 2 === 0;
  }
  return false;
}
// eslint-disable-next-line react/display-name
const Item = memo(({ id, item, itemRef, idx }: ItemPropTypes) => {
  const { isUsersComp } = item;

  // Redux
  const dispatch = useAppDispatch();
  const hoverId: string = useAppSelector((state) => state.ui.hoverId);
  const activeId: string = useAppSelector((state) => state.ui.activeId);
  const userPropertyIdx: number = useAppSelector((state) => state.comparables.userPropertyIdx);

  // Callbacks
  const handleMouseEnter = () => dispatch(setHoverId(id));
  const handleMouseLeave = () => dispatch(setHoverId(null));
  const handleMouseClick = () => dispatch(setActiveId(id));

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleMouseClick}
      ref={itemRef}
      css={{ cursor: 'pointer', flex: '0 0 auto', margin: '0 {spacing.100}' }}
    >
      {isUsersComp ? (
        <MyPropertyCard listing={item} isHovered={hoverId === id} lockHover={activeId === id} />
      ) : (
        <CompsCarouselCard
          listing={item}
          isHovered={hoverId === id}
          lockHover={activeId === id}
          isStriped={isStriped(idx, userPropertyIdx)}
        />
      )}
    </Box>
  );
});

export default Item;
